import { useMemo } from 'react';
import { KeyboardHandlers, useKeyboardEventHandler } from './useKeyboardEventHandler';

export const unselect = Symbol('unselect');
export type Unselect = typeof unselect;

/**
 * A hook that binds keyboard with onClick(id) handler and id based on type of key pressed
 * @param itemIds array of list item ids which is needed to determine order of ids
 * @param selectedId currently selection id which is needed to determine position of a selected item
 * @param onClick click handler which is triggered when movement keys (arrows, home/end) are pressed
 * @param onConfirm click handler which is triggered when enter key is pressed
 * @param onCancel click handler which is triggered when escape key is pressed
 * @returns props that have to be inserted into element intended to trigger events
 */
export const useListNavigation = (
  itemIds: string[],
  selectedId: string | Unselect,
  onClick: (itemId: string) => void,
  onConfirm?: () => void,
  onCancel?: () => void
) => {
  const index = selectedId === unselect ? -1 : itemIds.indexOf(selectedId);
  const end = itemIds.length - 1;
  const handlers: KeyboardHandlers = useMemo(
    () => ({
      onEnter: onConfirm,
      onSpace: onConfirm,
      onEscape: onCancel,
      onHome: () => onClick(itemIds[0]),
      onEnd: () => onClick(itemIds[end]),
      onDown: () => onClick(itemIds[(index + 1) % itemIds.length]),
      onUp: () => onClick(itemIds[(index - 1 + itemIds.length) % itemIds.length]),
    }),
    [onConfirm, onCancel, onClick, itemIds, end, index]
  );

  const onKeyDown = useKeyboardEventHandler(handlers);

  return {
    focusableProps: {
      tabIndex: 0,
      onKeyDown,
    },
  };
};
